/**
 * Translation at render time.
 *
 * Deliberately dependency-free and about 100 lines. The same file runs in two
 * React web apps, a React Native app and the Fastify API (which needs it to
 * validate CSV imports), so a library that assumes a bundler, a browser, or a
 * particular React version is not an option.
 *
 * THREE RULES
 *
 * 1. ENGLISH IS ALWAYS PRESENT. It ships inside the bundle, so a missing
 *    translation degrades to English rather than to a blank space or a raw key
 *    on a customer's screen. There is no loading state in which the interface
 *    has no words.
 *
 * 2. A TRANSLATION IS AN OVERLAY, never a replacement. Look up the locale, fall
 *    back to English, fall back to the key. A half-translated language is a
 *    perfectly usable language.
 *
 * 3. PLURALS USE Intl.PluralRules. Arabic has six plural forms and Urdu's
 *    differ from English's. `count === 1 ? 'seat' : 'seats'` is correct in
 *    exactly one language and quietly wrong in the rest.
 */
/** Languages written right to left. Roman Urdu is NOT one: it is Latin script. */
const RTL = new Set(['ar', 'ur', 'fa', 'he', 'ps', 'sd']);
export function isRtl(locale) {
    // ur-Latn must not inherit ur's direction, so match the full tag first and
    // only fall back to the base language when there is no script subtag.
    if (locale.includes('-')) {
        const [base, script] = locale.split('-');
        if (script && script.toLowerCase() === 'latn')
            return false;
        return RTL.has(base);
    }
    return RTL.has(locale);
}
export function dirFor(locale) {
    return isRtl(locale) ? 'rtl' : 'ltr';
}
/**
 * Substitute {{name}} placeholders.
 *
 * An unknown placeholder is left standing rather than blanked, because
 * "{{count}} seats" on screen tells you instantly that a variable was not
 * passed, whereas " seats" looks like a design choice and can survive to
 * production unnoticed.
 */
export function interpolate(template, vars) {
    if (!vars)
        return template;
    return template.replace(/\{\{(\w+)\}\}/g, (whole, name) => {
        const v = vars[name];
        return v === undefined || v === null ? whole : String(v);
    });
}
/** Placeholders a string depends on, used to validate a translation. */
export function placeholdersIn(template) {
    return [...new Set([...template.matchAll(/\{\{(\w+)\}\}/g)].map((m) => m[1]))].sort();
}
/**
 * A crude stand-in for `Intl.PluralRules` where the engine has none at all.
 *
 * Hermes in Expo Go builds without full ICU data, so `Intl.PluralRules` is
 * not just picky about locale tags -- it can be `undefined` outright, and
 * `new Intl.PluralRules('en')` throws "undefined is not a constructor" the
 * SAME way the original call did. A `catch` around the first call cannot
 * save the second one, which crashed the driver app's very first render.
 *
 * "one" for exactly 1, "other" otherwise: not correct for every language
 * (Arabic has six categories; this gives it one), but the lookup already
 * falls back through `${key}_other` to the bare key, so a wrong category
 * on an engine with no Intl at all is a worse translation, never a crash.
 */
function fallbackPlural(n) {
    return n === 1 ? 'one' : 'other';
}
export function makeTranslator({ locale, en, overrides, onMissing }) {
    const hasIntlPlural = typeof Intl !== 'undefined' && typeof Intl.PluralRules === 'function';
    // Intl.PluralRules can also throw on a tag it does not know; a bad locale
    // must not take the interface down, so fall back to English rules -- and
    // if the engine has no Intl.PluralRules at all, skip straight to the
    // manual fallback rather than trying (and failing) twice.
    let plural;
    if (!hasIntlPlural) {
        plural = { select: fallbackPlural };
    }
    else {
        try {
            plural = new Intl.PluralRules(locale);
        }
        catch {
            try {
                plural = new Intl.PluralRules('en');
            }
            catch {
                plural = { select: fallbackPlural };
            }
        }
    }
    return function t(key, vars) {
        let lookup = key;
        if (vars && typeof vars.count === 'number') {
            // "seatsFree" with count 3 tries seatsFree_other, then seatsFree_one on
            // languages whose rules name no other category, then the bare key.
            const category = plural.select(vars.count);
            const candidates = [`${key}_${category}`, `${key}_other`, key];
            lookup = candidates.find((c) => (overrides && c in overrides) || c in en) ?? key;
        }
        const template = overrides?.[lookup] ?? en[lookup];
        if (template === undefined) {
            onMissing?.(lookup);
            if (typeof vars?.fallback === 'string')
                return vars.fallback;
            // The key itself is the last resort. It is ugly on purpose: an untranslated
            // string should be obvious to whoever is looking at the screen, not blend in.
            return lookup;
        }
        return interpolate(template, vars);
    };
}
/** How much of a catalogue a locale covers. Drives the admin coverage screen. */
export function coverage(en, overrides) {
    const keys = Object.keys(en);
    const missing = keys.filter((k) => !overrides[k] || !overrides[k].trim());
    const translated = keys.length - missing.length;
    return {
        total: keys.length,
        translated,
        missing,
        percent: keys.length === 0 ? 100 : Math.round((translated / keys.length) * 100),
    };
}
//# sourceMappingURL=runtime.js.map